import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (container) => {
    if (!container) {
        return [];
    }

    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) => element.offsetParent !== null || element === document.activeElement
    );
};

/**
 * Traps Tab/Shift+Tab focus within `containerRef` while `isActive` is true, closes on Escape
 * (via `onClose`), moves focus into the container on activation (to `initialFocusRef` if
 * provided, otherwise the first focusable element), and restores focus to the element that
 * triggered activation once `isActive` becomes false.
 *
 * Works both for conditionally-rendered containers (mounted only while active) and
 * always-mounted containers whose visibility is toggled via CSS (e.g. the Virtual Librarian
 * widget), since activation is driven purely by the `isActive` flag rather than mount/unmount.
 */
export const useFocusTrap = ({ isActive, containerRef, onClose, initialFocusRef }) => {
    const triggerElementRef = useRef(null);

    useEffect(() => {
        if (!isActive) {
            return undefined;
        }

        triggerElementRef.current = document.activeElement;

        // Defer focus by two animation frames: for containers whose visibility is toggled via a
        // CSS transition (e.g. `visibility: hidden` -> `visible`, as with the Virtual Librarian
        // widget) rather than mount/unmount, a `visibility` transition only applies its new
        // computed value on the frame *after* the class change is committed to the DOM - calling
        // `.focus()` any earlier (including on the very next frame) silently no-ops because the
        // browser still considers the target non-focusable. A second frame ensures the browser
        // has actually flushed the transitioned style before we try to focus.
        let innerFrameId;
        const outerFrameId = requestAnimationFrame(() => {
            innerFrameId = requestAnimationFrame(() => {
                const focusTarget = initialFocusRef?.current || getFocusableElements(containerRef?.current)[0];
                if (focusTarget) {
                    focusTarget.focus();
                }
            });
        });

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.preventDefault();
                onClose?.();
                return;
            }

            if (event.key !== 'Tab') {
                return;
            }

            const focusableElements = getFocusableElements(containerRef?.current);
            if (focusableElements.length === 0) {
                return;
            }

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            cancelAnimationFrame(outerFrameId);
            cancelAnimationFrame(innerFrameId);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isActive, containerRef, onClose, initialFocusRef]);

    useEffect(() => {
        if (isActive) {
            return;
        }

        const triggerElement = triggerElementRef.current;
        if (triggerElement && typeof triggerElement.focus === 'function') {
            triggerElement.focus();
        }
        triggerElementRef.current = null;
    }, [isActive]);
};

export default useFocusTrap;
